import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Package, Shield, ExternalLink, Mail, Phone, Globe, Filter, Grid, List, Award, Users, CheckCircle, MessageCircle } from 'lucide-react';
import { useSuppliers, Supplier } from '../hooks/useSuppliers';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import { useSupabase } from '../contexts/SupabaseContext';
import ChatButton from '../components/chat/ChatButton';

const SupplierStorePage = () => {
  const { supplierSlug } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const { suppliers, getSupplierBySlug, loading: suppliersLoading } = useSuppliers();
  const { products, loading: productsLoading } = useProducts();
  const { user } = useSupabase();
  const [showChatButton, setShowChatButton] = useState(false);
  
  // Find the supplier
  const supplier = getSupplierBySlug(supplierSlug || '');
  
  // Filter products by this supplier
  const supplierProducts = products.filter(p => p.supplier_id === supplier?.id);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Package },
    { id: 'products', label: 'Products', icon: Grid },
    { id: 'about', label: 'About', icon: Users },
    { id: 'contact', label: 'Contact', icon: Mail }
  ];

  const handleVisitStore = () => {
    alert(`Visit supplier store: https://${supplier?.subdomain || 'supplier.madeinbd.com'}`);
  };

  const handleContactSupplier = () => {
    setShowChatButton(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Supplier Header */}
      <section className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Supplier Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-orange-100 to-red-100 w-24 h-24 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Package className="h-12 w-12 text-orange-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">{supplier?.organization || 'Supplier Name'}</h1>
                    {supplier?.verified && (
                      <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
                        <Shield className="h-4 w-4" />
                        <span>Verified</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-1">
                      <Star className="h-5 w-5 text-yellow-400 fill-current" />
                      <span className="font-medium">{supplier?.rating || 4.5}</span>
                      <span className="text-gray-600">({supplier?.total_products || 0} products)</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{supplier?.location || 'Location'}</span>
                    </div>
                    {supplier?.year_established && (
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>Est. {supplier.year_established}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-700 mb-4">{supplier?.description || 'Supplier description'}</p>
                  
                  {/* Specialties */}
                  <div className="flex flex-wrap gap-2">
                    {(supplier?.specialties || []).map((specialty, index) => (
                      <span key={index} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact & Actions */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Contact Supplier</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3 text-gray-600">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm">{supplier?.subdomain || 'supplier.madeinbd.com'}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">{supplier?.email || 'contact@supplier.com'}</span>
                </div>
                {supplier?.mobile && (
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">+880-{supplier.mobile}</span>
                  </div>
                )}
                {supplier?.phone && (
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">{supplier.phone}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={handleContactSupplier}
                  className="w-full bg-madeinbd-gradient text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Chat with Supplier</span>
                </button>
                <button 
                  onClick={handleVisitStore}
                  className="w-full border border-orange-600 text-orange-600 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span>Visit Store</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Products */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Main Products & Services</h2>
                  <p className="text-gray-700 leading-relaxed">{supplier?.products || 'Supplier products description'}</p>
                </div>

                {/* Company Highlights */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Company Highlights</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Verified Supplier</div>
                        <div className="text-sm text-gray-600">Authenticated business credentials</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Award className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Quality Certified</div>
                        <div className="text-sm text-gray-600">Meets international standards</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <Users className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Experienced Team</div>
                        <div className="text-sm text-gray-600">Skilled craftsmen and professionals</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <Globe className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">Export Ready</div>
                        <div className="text-sm text-gray-600">International shipping available</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Sidebar */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Supplier Statistics</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rating</span>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="font-medium">{supplier?.rating || 4.5}</span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Products</span>
                      <span className="font-medium">{supplier?.total_products || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Response Rate</span>
                      <span className="font-medium">98%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Response Time</span>
                      <span className="font-medium">{"< 24h"}</span>
                    </div>
                    {supplier?.year_established && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Years in Business</span>
                        <span className="font-medium">{new Date().getFullYear() - supplier.year_established}+ years</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Choose Us?</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Verified business credentials</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Quality assurance guarantee</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Competitive pricing</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Fast response time</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>International shipping</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-6">Product Catalog</h2>
              
              {productsLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : supplierProducts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {supplierProducts.map(product => (
                    <ProductCard key={product.id} product={{
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      originalPrice: product.original_price || undefined,
                      image: product.image_url || 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg',
                      rating: product.rating,
                      reviews: product.reviews,
                      seller: supplier?.organization || 'Supplier',
                      location: supplier?.location || 'Bangladesh',
                      badge: product.badge || undefined
                    }} />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                  <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Product Catalog Coming Soon</h3>
                  <p className="text-gray-600 mb-6">Contact the supplier directly for detailed product information and catalogs.</p>
                  <button 
                    onClick={handleContactSupplier}
                    className="bg-madeinbd-gradient text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  >
                    Request Product Catalog
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">About {supplier?.organization || 'Supplier'}</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Company Overview</h3>
                  <p className="text-gray-700 leading-relaxed">{supplier?.description || 'Company description'}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Products & Services</h3>
                  <p className="text-gray-700 leading-relaxed">{supplier?.products || 'Products description'}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Specializations</h3>
                  <div className="flex flex-wrap gap-2">
                    {(supplier?.specialties || []).map((specialty, index) => (
                      <span key={index} className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600">Representative:</span>
                      <p className="font-medium">{supplier?.representative || 'Representative name'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Location:</span>
                      <p className="font-medium">{supplier?.location || 'Location'}</p>
                    </div>
                    {supplier?.year_established && (
                      <div>
                        <span className="text-sm text-gray-600">Established:</span>
                        <p className="font-medium">{supplier.year_established}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-sm text-gray-600">Business Type:</span>
                      <p className="font-medium">Manufacturer & Exporter</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Company</h3>
                    <p className="text-gray-700">{supplier?.organization || 'Company name'}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Representative</h3>
                    <p className="text-gray-700">{supplier?.representative || 'Representative name'}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Address</h3>
                    <p className="text-gray-700">{supplier?.address || 'Address'}</p>
                  </div>
                  
                  {supplier?.email && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                      <p className="text-gray-700">{supplier.email}</p>
                    </div>
                  )}
                  
                  {supplier?.mobile && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Mobile</h3>
                      <p className="text-gray-700">+880-{supplier.mobile}</p>
                    </div>
                  )}
                  
                  {supplier?.phone && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                      <p className="text-gray-700">{supplier.phone}</p>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Website</h3>
                    <p className="text-gray-700">{supplier?.subdomain || 'supplier.madeinbd.com'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Send Message</h2>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                    <input 
                      type="text" 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input 
                      type="email" 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <input 
                      type="text" 
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter subject"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea 
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your message"
                    />
                  </div>
                  
                  <button 
                    type="button"
                    onClick={handleContactSupplier}
                    className="w-full bg-madeinbd-gradient text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Chat Button with Supplier */}
      {showChatButton && supplier?.user_id && (
        <ChatButton initialUserId={supplier.user_id} initialTopic={`Inquiry about ${supplier.organization}`} />
      )}
    </div>
  );
};

export default SupplierStorePage;